import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, Card, StatusDot, freshness, livenessState } from '@diwata/ironvale'
import type { StatusDotState } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { useUI } from '@/store/ui'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import {
  EFFECT_CLASSES,
  canAddScope,
  principalLane,
  type EffectClass,
  type Lane,
} from '@/lib/capabilitySplit'
import {
  OUTER_EVENT,
  listOuter,
  mintOuter,
  revokeOuter,
  type OuterPrincipal,
} from '@/lib/outerPrincipals'
import { EGRESS_EVENT, listCrossings, type EgressCrossing } from '@/lib/egressLog'
import './missions-agents.css'

// Agents = Familiars. The ledger is the page's spine: every provenance-stamped
// act the ✦ Familiar performed, newest first. Tiers follow Conclave
// (Sovereign / Adept / Guild); external model providers are backends.

interface LedgerAct {
  id: string
  at: string
  kind: 'tool' | 'write' | 'mission' | 'proposal'
  summary: string
  subject_type: string
  subject_id: string | null
  domain: string | null
  status: string | null
  detail: Record<string, unknown>
}

interface LedgerPage {
  acts: LedgerAct[]
  more: boolean
  next_before: string | null
}

const KIND_GLYPH: Record<LedgerAct['kind'], string> = {
  tool: '✦',
  write: '✦',
  mission: '◆',
  proposal: '◈',
}

// Plain-words for raw run/act states — never show the machine token in the ledger.
const ACT_STATUS_WORD: Record<string, string> = {
  pending: 'not started',
  running: 'working…',
  awaiting_input: 'waiting on input',
  awaiting_gate: 'waiting for your call',
  done: 'done',
  ok: 'done',
  failed: 'hit a snag',
}

const FILTERS: { key: string | null; label: string }[] = [
  { key: null, label: 'Everything' },
  { key: 'tool', label: 'Tools used' },
  { key: 'write', label: 'Writes' },
  { key: 'mission', label: 'Missions' },
  { key: 'proposal', label: 'Asks' },
]

function subjectHref(act: LedgerAct): string | null {
  if (act.subject_type === 'mission') return '/missions'
  if (act.subject_type === 'entity') return '/graph'
  if (act.subject_type === 'signal') return '/signals'
  return null
}

function when(iso: string): string {
  const d = new Date(iso)
  const mins = Math.round((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`
  return d.toLocaleDateString()
}

function FamiliarLedger() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [kind, setKind] = useState<string | null>(null)
  const [extra, setExtra] = useState<LedgerAct[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [exhausted, setExhausted] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-ledger', kind],
    queryFn: () => api.get<LedgerPage>(`/agent/ledger${kind ? `?kind=${kind}` : ''}`),
  })

  // page 1 can refetch (window focus) while extra pages are loaded — dedupe by id
  const seen = new Set<string>()
  const acts = [...(data?.acts ?? []), ...extra].filter((a) => {
    if (seen.has(a.id)) return false
    seen.add(a.id)
    return true
  })
  const nextBefore = exhausted ? null : (cursor ?? data?.next_before ?? null)

  function setFilter(k: string | null) {
    setKind(k)
    setExtra([])
    setCursor(null)
    setExhausted(false)
    setMoreError(false)
  }

  async function loadMore() {
    if (!nextBefore || loadingMore) return
    const requestKind = kind // a filter change mid-flight must not mix streams
    setLoadingMore(true)
    setMoreError(false)
    try {
      const qs = new URLSearchParams()
      if (requestKind) qs.set('kind', requestKind)
      qs.set('before', nextBefore)
      const page = await api.get<LedgerPage>(`/agent/ledger?${qs}`)
      setKind((current) => {
        if (current === requestKind) {
          setExtra((p) => [...p, ...page.acts])
          if (page.more) setCursor(page.next_before)
          else setExhausted(true)
        }
        return current
      })
    } catch {
      setMoreError(true)
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <Card style={{ marginBottom: 26 }}>
      <p className="dw-inbox-detail" style={{ marginTop: 0, marginBottom: 10 }}>
        Everything ✦ Tala did, wrote, and asked — every act is provenance-stamped.
      </p>
      <div className="dw-chip-row" style={{ marginBottom: 12 }}>
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            className={`dw-chip dw-chip--toggle${kind === f.key ? ' dw-chip--on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {isLoading && <p className="dw-empty">Loading…</p>}
      {isError && <p className="dw-error">Couldn't load the ledger — try again in a moment.</p>}
      {!isLoading && !isError && acts.length === 0 && (
        <p className="dw-empty">
          No acts yet — Tala's work lands here as it happens. Run a mission or ask her something.
        </p>
      )}
      <div style={{ display: 'grid', gap: 6 }}>
        {acts.map((a) => {
          const href = subjectHref(a)
          return (
            <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 'var(--ae-font-size-xs)' }}>
              <span aria-hidden style={{ opacity: 0.8 }}>{KIND_GLYPH[a.kind]}</span>
              <span style={{ flex: 1 }}>
                {a.summary}
                {a.domain && <span className="dw-inbox-detail"> · {a.domain}</span>}
                {a.status && <span className="dw-inbox-detail"> · {ACT_STATUS_WORD[a.status] ?? a.status}</span>}
                {href && (
                  <>
                    {' '}
                    <a className="dw-link" href={href}>open →</a>
                  </>
                )}
              </span>
              <span className="dw-inbox-detail" style={{ whiteSpace: 'nowrap' }}>{when(a.at)}</span>
            </div>
          )
        })}
      </div>
      {nextBefore && (
        <Button size="sm" variant="ghost" style={{ marginTop: 10 }} disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? 'Loading…' : 'Earlier acts'}
        </Button>
      )}
      {moreError && <p className="dw-error" style={{ marginTop: 6 }}>Couldn't load earlier acts — try again.</p>}
    </Card>
  )
}

interface KeyState {
  connected: boolean
  provider: string
  last4: string | null
  connected_at: string | null
  needs_reentry: boolean
}

interface UsageSummary {
  month: string
  calls: number
  input_tokens: number
  output_tokens: number
  by_purpose: { purpose: string; calls: number; input_tokens: number; output_tokens: number }[]
  by_source: Record<string, number>
}

// Human names for the meter's purpose codes — the vocabulary of "what ran".
const PURPOSE_LABEL: Record<string, string> = {
  ask: 'Questions answered',
  compose: 'Missions composed',
  mission_step: 'Mission steps',
  mission_revise: 'Artifact revisions',
  toolloop: 'Tool-loop turns',
  trove_triage: 'Trove triage',
  trove_curation: 'Trove curation',
}

function ModelBackend() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { data: key, isLoading: keyLoading, isError: keyError } = useQuery({
    queryKey: ['model-key'],
    queryFn: () => api.get<KeyState>('/agent/model-key'),
  })
  const { data: usage } = useQuery({
    queryKey: ['model-usage'],
    queryFn: () => api.get<UsageSummary>('/agent/usage'),
  })
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function connect() {
    if (!draft.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      await api.put('/agent/model-key', { key: draft.trim() })
      setDraft('')
      await qc.invalidateQueries({ queryKey: ['model-key'] })
    } catch (err) {
      const status = (err as { status?: number }).status
      if (status === 400) setError("That doesn't look like an Anthropic key (sk-ant-…) — nothing was saved.")
      else if (status === 503) setError('This server is not configured for key custody yet — nothing was saved.')
      else setError("Couldn't save the key — try again.")
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.del('/agent/model-key')
      await qc.invalidateQueries({ queryKey: ['model-key'] })
    } catch {
      setError("Couldn't remove the key — try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 26 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
          <div>
            <div style={{ fontWeight: 600, letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: 7 }}>
              {/* Liveness of the model backend: your key wired → live; readable-but-stale → idle; house key → neutral. */}
              <StatusDot
                state={key?.connected ? (key.needs_reentry ? 'idle' : 'live') : 'neutral'}
                size="sm"
                label={key?.connected ? (key.needs_reentry ? 'Model backend — key needs re-entry' : 'Model backend — running on your key') : 'Model backend — house key'}
              />
              Anthropic Claude
            </div>
            <div style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ae-color-text-muted)', marginTop: 3 }}>Model backend</div>
          </div>
          {!keyLoading && !keyError && (
            <Badge variant={key?.connected ? (key.needs_reentry ? 'info' : 'success') : 'neutral'}>
              {key?.connected ? (key.needs_reentry ? 'Re-enter key' : 'Your key') : 'House key'}
            </Badge>
          )}
        </div>
        {keyError && <p className="dw-error" style={{ fontSize: 'var(--ae-font-size-xs)' }}>Couldn't check the key state — reload to retry.</p>}
        {key?.connected ? (
          <div style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            {key.needs_reentry ? (
              <p style={{ marginBottom: 8 }} className="dw-error">
                Your stored key (…{key.last4}) can't be read any more — the server's encryption secret
                changed. Remove it and paste the key again; until then the house key answers.
              </p>
            ) : (
              <p style={{ marginBottom: 8 }}>
                Connected — <span style={{ fontFamily: 'var(--ae-font-mono)' }}>…{key.last4}</span>. Your familiar
                runs on your key; the meter records what it spends.
              </p>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={disconnect}>Remove key</Button>
          </div>
        ) : (
          <div style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            <p style={{ marginBottom: 8, color: 'var(--ae-color-text-secondary)' }}>
              Bring your own Claude key and your familiar runs on it. Stored encrypted; shown as last-4 only;
              never sent anywhere but Anthropic.
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="dw-input"
                type="password"
                placeholder="sk-ant-…"
                value={draft}
                aria-label="Anthropic API key"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                onChange={(e) => setDraft(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button size="sm" variant="primary" disabled={busy || !draft.trim()} onClick={connect}>
                {busy ? 'Saving…' : 'Connect'}
              </Button>
            </div>
          </div>
        )}
        {error && <div className="dw-error" style={{ marginTop: 6, fontSize: 'var(--ae-font-size-xs)' }}>{error}</div>}
      </Card>
      <Card>
        <div style={{ fontWeight: 600, letterSpacing: '0.03em', marginBottom: 6 }}>
          This month's spend <span className="dw-inbox-detail">({usage?.month ?? '…'})</span>
        </div>
        {usage && usage.calls === 0 && (
          <p className="dw-empty" style={{ fontSize: 'var(--ae-font-size-xs)' }}>No model calls yet this month.</p>
        )}
        {usage && usage.calls > 0 && (
          <div style={{ fontSize: 'var(--ae-font-size-xs)', display: 'grid', gap: 4 }}>
            <div>
              {usage.calls} calls · {usage.input_tokens.toLocaleString()} in / {usage.output_tokens.toLocaleString()} out
              {Object.keys(usage.by_source).length > 0 && (
                <span className="dw-inbox-detail">
                  {' '}· {Object.entries(usage.by_source).map(([s, n]) => `${n} on ${s === 'user' ? 'your key' : 'house key'}`).join(', ')}
                </span>
              )}
            </div>
            {usage.by_purpose.map((p) => (
              <div key={p.purpose} className="dw-inbox-detail">
                {PURPOSE_LABEL[p.purpose] ?? p.purpose} — {p.calls} · {(p.input_tokens + p.output_tokens).toLocaleString()} tokens
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

interface Principal {
  id: string
  name: string
  prefix: string
  scopes: string[]
  created_at: string | null
  last_used_at: string | null
  revoked: boolean
}

const SCOPE_HELP: Record<string, string> = {
  read: 'see your world',
  propose: 'suggest at your gate',
  attach: 'add evidence to steps',
  egress: 'send out of your world',
}

// The three lanes of the capability split, each with its plain badge + one-line gloss.
const LANE_META: Record<Lane, { badge: string; title: string; blurb: string }> = {
  inner: {
    badge: 'Inner',
    title: 'Inner — context-bearing',
    blurb: 'Reads or acts inside your world.',
  },
  familiar: {
    badge: 'Familiar',
    title: "Tala's hand",
    blurb: 'The drafting hand — never handed out.',
  },
  outer: {
    badge: 'Outer',
    title: 'Outer — egress-bearing',
    blurb: 'Sends out of your world, only at your gate.',
  },
}

const GRANT_TAG: Record<EffectClass['grant'], string> = {
  inner: 'you can grant',
  'outer-gated': 'Outer only · gated',
  familiar: "Tala's hand only",
}

const LANE_ORDER: Lane[] = ['inner', 'familiar', 'outer']

/** The effect classes as the capability-split surface: grouped by lane, each scope carrying its
 *  Inner / Outer / Familiar badge and plain gloss. This is where the doctrine becomes legible. */
function ScopeLegend() {
  return (
    <div className="dw-lanes">
      {LANE_ORDER.map((lane) => (
        <div key={lane} className={`dw-lane dw-lane--${lane}`}>
          <div className="dw-lane-head">
            <span className={`dw-lane-badge dw-lane-badge--${lane}`}>{LANE_META[lane].badge}</span>
            <span className="dw-lane-title">{LANE_META[lane].title}</span>
            <span className="dw-lane-blurb">{LANE_META[lane].blurb}</span>
          </div>
          <div className="dw-scopes">
            {EFFECT_CLASSES.filter((e) => e.lane === lane).map((e) => (
              <div key={e.key} className={`dw-scope dw-scope--${e.grant}`}>
                <span className="dw-scope-name">{e.key}</span>
                <span className="dw-scope-what">{e.what}</span>
                <span className={`dw-scope-tag dw-scope-tag--${e.grant}`}>{GRANT_TAG[e.grant]}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/** The invariant, stated where you can't miss it: a principal is Inner OR Outer, never both. */
function SplitInvariant() {
  return (
    <div className="dw-invariant">
      <div className="dw-invariant-mark" aria-hidden="true">
        <span className="dw-lane-badge dw-lane-badge--inner">Inner</span>
        <span className="dw-invariant-bar">reads your world</span>
        <span className="dw-invariant-x">✕</span>
        <span className="dw-invariant-bar">sends out of it</span>
        <span className="dw-lane-badge dw-lane-badge--outer">Outer</span>
      </div>
      <p className="dw-invariant-copy">
        An agent that can read your world can't also send out of it — that's the split that keeps
        your context yours. Every principal is <em>Inner</em> or <em>Outer</em>, never both, so no
        one hand ever holds your context <em>and</em> a way out.
      </p>
    </div>
  )
}

const laneBadge = (lane: Lane | null) =>
  lane === 'inner' || lane === 'outer' ? (
    <span className={`dw-lane-badge dw-lane-badge--${lane}`}>{lane === 'inner' ? 'Inner' : 'Outer'}</span>
  ) : null

/** A grantable scope checkbox — disabled (with a reason) when ticking it would cross the membrane. */
function ScopeCheck({
  scope,
  scopes,
  onToggle,
}: {
  scope: string
  scopes: string[]
  onToggle: (checked: boolean) => void
}) {
  const checked = scopes.includes(scope)
  const blocked = !checked && !canAddScope(scopes, scope)
  return (
    <label
      className={`dw-scope-pick${blocked ? ' dw-scope-pick--blocked' : ''}`}
      title={
        blocked
          ? scope === 'egress'
            ? 'Can’t add egress — this agent already reads your world.'
            : 'Can’t add a context scope — this agent can already send out of your world.'
          : SCOPE_HELP[scope]
      }
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={blocked}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <HelpHint gloss={SCOPE_HELP[scope]}>{scope}</HelpHint>
      <span className="dw-inbox-detail">({SCOPE_HELP[scope]})</span>
    </label>
  )
}

/** Named non-human actors with scoped keys — the human face of the agent protocol, now split
 *  Inner vs Outer. Inner principals are minted server-side (they can never hold egress); Outer
 *  principals hold egress alone and are modelled on-device until the extension lands the wire. */
function Principals() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['agent-principals'],
    queryFn: () => api.get<{ principals: Principal[] }>('/agent/principals'),
  })
  const [outer, setOuter] = useState<OuterPrincipal[]>(() => listOuter())
  useEffect(() => {
    const refresh = () => setOuter(listOuter())
    window.addEventListener(OUTER_EVENT, refresh)
    return () => window.removeEventListener(OUTER_EVENT, refresh)
  }, [])

  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['read'])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bornKey, setBornKey] = useState<string | null>(null)

  const kind = principalLane(scopes) // 'inner' | 'outer' | null

  function toggleScope(sc: string, checked: boolean) {
    setScopes((cur) => (checked ? [...cur, sc] : cur.filter((x) => x !== sc)))
  }

  async function create() {
    if (busy || !name.trim() || scopes.length === 0 || !kind) return
    setBusy(true)
    setError(null)
    try {
      if (kind === 'outer') {
        // Egress custody isn't on the wire yet (the CDS backend refuses to grant it), so an
        // Outer principal is minted on-device — the split made physical.
        const { key } = mintOuter(name.trim(), scopes)
        setBornKey(key)
        setOuter(listOuter())
      } else {
        const made = await api.post<Principal & { key: string }>('/agent/principals', {
          name: name.trim(),
          scopes,
        })
        setBornKey(made.key)
        await qc.invalidateQueries({ queryKey: ['agent-principals'] })
      }
      setName('')
    } catch {
      setError("Couldn't mint the principal — check the name and scopes.")
    } finally {
      setBusy(false)
    }
  }

  async function revoke(p: Principal | OuterPrincipal) {
    setError(null)
    try {
      if ('outer' in p) {
        revokeOuter(p.id)
        setOuter(listOuter())
      } else {
        await api.del(`/agent/principals/${p.id}`)
        await qc.invalidateQueries({ queryKey: ['agent-principals'] })
      }
    } catch {
      setError("Couldn't revoke — try again.")
    }
  }

  const inner = data?.principals ?? []
  const rows: (Principal | OuterPrincipal)[] = [...inner, ...outer]

  return (
    <Card style={{ marginBottom: 26 }}>
      <p className="dw-teach-lede" style={{ marginTop: 0 }}>
        A principal is an agent you've authorized to act — a named key with scoped permissions.
      </p>
      <p className="dw-principal-teach">
        Diwa's own familiar ✦ Tala works inside your gates. Anything <em>outside</em> — a research
        script, another runtime, your Sovereign familiar — reaches in only as a principal, and only
        as far as the scopes you grant. Scopes are the law — and the law is split.
      </p>

      <ScopeLegend />
      <SplitInvariant />

      {rows.length === 0 && (
        <p className="dw-empty" style={{ marginBottom: 10 }}>
          No principals yet — mint one when an external agent or script needs scoped reach into, or
          a gated way out of, your world.
        </p>
      )}
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {rows.map((p) => {
          const lane = 'outer' in p ? 'outer' : principalLane(p.scopes) ?? 'inner'
          return (
            <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 'var(--ae-font-size-xs)', opacity: p.revoked ? 0.5 : 1 }}>
              {/* Liveness from real activity: revoked → error; else recency of last_used_at drives live/idle/neutral. */}
              <StatusDot
                state={p.revoked ? 'error' : livenessState(p.last_used_at)}
                size="sm"
                label={p.revoked ? `${p.name} — revoked` : `${p.name} — last used ${freshness(p.last_used_at)}`}
                style={{ alignSelf: 'center' }}
              />
              {laneBadge(lane)}
              <span style={{ fontFamily: 'var(--ae-font-mono)' }}>{p.prefix}…</span>
              <span style={{ flex: 1 }}>
                {p.name}
                <span className="dw-inbox-detail"> · {p.scopes.join(' + ') || 'no scopes'}</span>
                {'outer' in p && <span className="dw-inbox-detail"> · queued for your extension</span>}
                {p.revoked && <span className="dw-inbox-detail"> · revoked</span>}
                {!p.revoked && p.last_used_at && (
                  <span className="dw-inbox-detail"> · last used {freshness(p.last_used_at)}</span>
                )}
                {!p.revoked && !p.last_used_at && !('outer' in p) && (
                  <span className="dw-inbox-detail"> · never used</span>
                )}
              </span>
              {!p.revoked && (
                <button type="button" className="dw-inline-link" aria-label={`revoke ${p.name}`}
                        onClick={() => revoke(p)}>
                  revoke
                </button>
              )}
            </div>
          )
        })}
      </div>
      {bornKey && (
        <div className="dw-rail-empty" style={{ marginBottom: 10, fontSize: 'var(--ae-font-size-xs)' }}>
          Copy this key now — it will never be shown again:{' '}
          <code style={{ fontFamily: 'var(--ae-font-mono)', userSelect: 'all' }}>{bornKey}</code>{' '}
          <button type="button" className="dw-inline-link" onClick={() => setBornKey(null)}>done</button>
        </div>
      )}
      <div className="dw-mint">
        <div className="dw-mint-row">
          <input className="dw-input" placeholder="Name — e.g. research script" value={name}
                 aria-label="Principal name" maxLength={100} style={{ width: 220 }}
                 onChange={(e) => setName(e.target.value)} />
          {['read', 'propose', 'attach', 'egress'].map((sc) => (
            <ScopeCheck key={sc} scope={sc} scopes={scopes} onToggle={(c) => toggleScope(sc, c)} />
          ))}
        </div>
        <div className="dw-mint-foot">
          {kind && (
            <span className="dw-mint-kind">
              {laneBadge(kind)}{' '}
              {kind === 'outer'
                ? 'Outer agent — holds egress alone; sends only when you release it at the gate.'
                : 'Inner agent — reads and acts inside your world; can never be handed a way out.'}
            </span>
          )}
          {!kind && scopes.length > 0 && (
            <span className="dw-mint-kind dw-error">Pick scopes on one side of the split — Inner or Outer, not both.</span>
          )}
          <span style={{ flex: 1 }} />
          <Button size="sm" variant="primary" disabled={busy || !name.trim() || !kind} onClick={create}>
            {busy ? 'Minting…' : kind === 'outer' ? 'Mint Outer principal' : 'Mint principal'}
          </Button>
        </div>
        {error && <span className="dw-error" style={{ fontSize: 'var(--ae-font-size-xs)' }}>{error}</span>}
      </div>
    </Card>
  )
}

function crossingWhen(iso: string): string {
  const d = new Date(iso)
  const mins = Math.round((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`
  return d.toLocaleDateString()
}

/** The border-crossings log — every outbound send you released at a gate, surfaced as evidence
 *  beside Tala's ledger. The send itself runs later in your browser extension; the record is now. */
function OutboundCrossings() {
  const [crossings, setCrossings] = useState<EgressCrossing[]>(() => listCrossings())
  useEffect(() => {
    const refresh = () => setCrossings(listCrossings())
    window.addEventListener(EGRESS_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(EGRESS_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  return (
    <Card style={{ marginBottom: 26 }}>
      <p className="dw-inbox-detail" style={{ marginTop: 0, marginBottom: 10 }}>
        Every outbound send you released at a gate — logged as evidence. Nothing crosses this border
        without your approval, and the send itself runs in your browser extension (coming).
      </p>
      {crossings.length === 0 ? (
        <p className="dw-empty">
          No crossings yet — approve an outbound action at a mission gate and the release lands here.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {crossings.map((c) => (
            <div key={c.id} className="dw-crossing">
              <span className="dw-lane-badge dw-lane-badge--outer" style={{ alignSelf: 'center' }}>Outer</span>
              <div className="dw-crossing-body">
                <div className="dw-crossing-head">
                  <span className="dw-crossing-channel">{c.channel}</span>
                  <span className="dw-crossing-arrow" aria-hidden="true">→</span>
                  <span className="dw-crossing-to">{c.to}</span>
                  {c.subject && <span className="dw-inbox-detail"> · {c.subject}</span>}
                </div>
                <div className="dw-crossing-preview">{c.preview}</div>
                <div className="dw-inbox-detail">
                  from <a className="dw-link" href={`/missions/${c.missionId}`}>{c.missionTitle}</a> · {c.step}
                </div>
              </div>
              <div className="dw-crossing-meta">
                <Badge variant="info">queued</Badge>
                <span className="dw-inbox-detail" style={{ whiteSpace: 'nowrap' }}>{crossingWhen(c.at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

interface AgentCard {
  name: string
  kind: string
  description: string
  status: 'connected' | 'available' | 'soon'
}

// The taxonomy (founder ruling 2026-07-27): familiars are first-class citizens
// in tiers — Platform (a product's own, the drafting hand inside it), Sovereign
// (the keeper's own, lives on the sovereign plane, reaches in as a trusted
// principal), Adept (one task), Guild (an org's). External agents — Claude,
// OpenAI, open models, other runtimes — connect as principals, never as familiars.
const FAMILIARS: AgentCard[] = [
  { name: '✦ Tala', kind: 'Platform familiar', description: "Diwa's own familiar, named for the goddess of stars — the drafting hand inside this platform. She runs the matcher, missions, and reviews on your world; every act lands in her ledger above. Included in your plan.", status: 'connected' },
  { name: 'Adept', kind: 'Specialist', description: 'One-task familiars the platform familiar spawns and delegates to — the protocol steps (bid/no-bid, rule review) are their early form.', status: 'soon' },
  { name: 'Sovereign', kind: 'Yours, everywhere', description: 'Your personal familiar — one per keeper, lives on the sovereign plane, follows you across platforms. It reaches into Diwa as a trusted principal with a scoped key. Bring your own.', status: 'soon' },
  { name: 'Guild', kind: 'Organisational', description: 'A shared familiar for a team or company — knows collective context; joined via a Bond. Bring your own.', status: 'soon' },
]

const STATUS: Record<AgentCard['status'], { label: string; variant: 'success' | 'info' | 'neutral'; dot: StatusDotState }> = {
  connected: { label: 'Active', variant: 'success', dot: 'live' },
  available: { label: 'Available', variant: 'info', dot: 'idle' },
  soon: { label: 'Soon', variant: 'neutral', dot: 'neutral' },
}

function AgentGrid({ items }: { items: AgentCard[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
      {items.map((a) => {
        const s = STATUS[a.status]
        return (
          <Card key={a.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 600, letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <StatusDot state={s.dot} size="sm" label={`${a.name} — ${s.label}`} />
                  {a.name}
                </div>
                <div style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ae-color-text-muted)', marginTop: 3 }}>{a.kind}</div>
              </div>
              <Badge variant={s.variant}>{s.label}</Badge>
            </div>
            <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', lineHeight: 1.5 }}>{a.description}</div>
          </Card>
        )
      })}
    </div>
  )
}

export function Agents() {
  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Agents{' '}
            <HelpHint gloss="An actor that can act in your world. Diwa's own is the familiar ✦ Tala; outside agents — Claude, scripts, your own Sovereign — connect as scoped principals." />{' '}
            <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">Familiars are first-class citizens, governed by Conclave. Any agent — Claude, OpenAI, open models, your own Sovereign — connects as a scoped <HelpHint term="principal">principal</HelpHint>, on one side of the capability split: <em>Inner</em> (reads your world) or <em>Outer</em> (sends out of it), never both.</div>
        </div>
      </div>

      <div className="dw-section-title" style={{ marginBottom: 10 }}>Tala's ledger</div>
      <FamiliarLedger />

      <div className="dw-section-title" style={{ marginBottom: 10 }}>Outbound crossings</div>
      <OutboundCrossings />

      <div className="dw-section-title" style={{ marginBottom: 10 }}>Model backend &amp; spend</div>
      <ModelBackend />

      <div className="dw-section-title" style={{ marginBottom: 10 }}>Principals</div>
      <Principals />

      <div className="dw-section-title" style={{ marginBottom: 10 }}>Familiars</div>
      <div style={{ marginBottom: 26 }}>
        <AgentGrid items={FAMILIARS} />
      </div>
    </div>
  )
}
